<template>
	<main-page :user="user" color="orange">
		<template v-slot:title>
			PROFILE OVERVIEW
		</template>

		<div class="container">
			<div class="row content">
				<div class="col-4 zone-padding pt-4 text-left">
					<h1 class="mb-2 si-font si-text-green">Skills & Mindsets</h1>
					<config-select
						class="vs-big"
						:options="itemOptions"
						:clearable="false"
						:nullOption="true"
						nullText="All"
						sortBy="sequence"
						v-model="selectedItem"
					/>

					<h1 class="mt-4 mb-2 si-font si-text-blue">Team</h1>
					<v-select
						class="vs-big"
						:options="teamOptions"
						label="name"
						:clearable="false"
						v-model="selectedTeam"
						:reduce="(obj) => obj.id"
					/>

					<h1 class="mt-4 mb-2 si-font si-text-yellow">Student</h1>
					<div style="display:inline-block">
						<div class="key-text">
							<div class="card clickable-card done key-color"></div>
							Complete
						</div>
						<div class="key-text">
							<div class="card clickable-card partial key-color"></div>
							Incomplete
						</div>
						<div class="key-text">
							<div class="card clickable-card nodata key-color"></div>
							No responses
						</div>
					</div>
					<div
						v-if="studentOptions && studentOptions.length > 0"
						class="card mt-2 clickable clickable-card"
						:class="{ selected: !selectedStudent }"
						@click="selectedStudent = null"
					>
						<div class="d-flex flex-row align-items-center mt-1 px-2">
							<div class="flex-grow">
								<div class="flex-grow text-left text-md py-2">
									All Students
								</div>
							</div>
						</div>
					</div>
					<div
						v-for="student in studentOptions"
						:key="student.id"
						class="card mt-2 clickable clickable-card"
						:class="[{ selected: student.id == selectedStudent }, getColor(student)]"
					>
						<div
							v-if="getColor(student) != 'nodata'"
							class="d-flex flex-row align-items-center mt-1 px-2"
							@click="selectedStudent = student.id">
							<div class="flex-grow" style="max-width: 90%">
								<div class="flex-grow text-left text-md">
									{{ fs.displayName(student.name) }}
								</div>
								<div class="mb-1 text-muted text-left">{{ student.email }}</div>
							</div>
						</div>
						<div 
							v-if="getColor(student) == 'nodata'"
							class="d-flex flex-row align-items-center mt-1 px-2"
							disabled>
							<div class="flex-grow" style="max-width: 90%">
								<div class="flex-grow text-left text-md">
									{{ fs.displayName(student.name) }}
								</div>
								<div class="mb-1 text-muted text-left">{{ student.email }}</div>
							</div>
						</div>
					</div>
					<div
						v-if="!(studentOptions && studentOptions.length > 0)"
						class="text-center text-very-muted text-lg _800 mt-2"
					>
						No students
					</div>
				</div>
				<div class="col-8 zone-padding text-left d-flex flex-column justify-content-center h-100">
					<apex-chart
						v-if="chartData.length <= 100"
						ref="chart"
						type="line"
						:options="chartOptions"
						:series="chartData"
						height="500px;"
					></apex-chart>
					<div v-if="chartData.length > 100" class="card p-5 mb-5">
						<div class="text-center text-very-muted text-lg _800">
							Too much data for line graph
						</div>
						<div class="text-center text-very-muted text-lg _800">
							Export to view data
						</div>
					</div>
					<div v-if="showStats" class="ml-5 px-4 py-2 text-md border">
						<div class="text-center">
							<h3 class="my-0 si-font text-center" style="display: inline-block;">STATISTICS</h3>
							<span>, {{ getTitle() }}</span>
						</div>
						<div>
							<strong>Range: </strong>
							<span v-if="rangeLow != rangeHigh"
								>{{ fs.round1d(rangeLow) }} to {{ fs.round1d(rangeHigh) }}</span
							>
							<span v-if="rangeLow == rangeHigh">{{ fs.round1d(rangeLow) }}</span> -
							<span v-if="fs.profileScoreDescription(rangeLow) != fs.profileScoreDescription(rangeHigh)">
								"{{ fs.profileScoreDescription(rangeLow) }}" to "{{
									fs.profileScoreDescription(rangeHigh)
								}}"
							</span>
							<span v-if="fs.profileScoreDescription(rangeLow) == fs.profileScoreDescription(rangeHigh)">
								"{{ fs.profileScoreDescription(rangeLow) }}"
							</span>
						</div>
						<div class="d-flex flex-row justify-content-between">
							<div class="d-flex flex-row align-items-center">
								<strong>Mean:</strong><span class="ml-1">{{ fs.round1d(mean) }}</span>
							</div>
							<div class="d-flex flex-row align-items-center ml-3">
								<strong>Median:</strong><span class="ml-1">{{ fs.round1d(median) }}</span>
							</div>
							<div class="d-flex flex-row align-items-center ml-3">
								<template v-if="selectedItem">
									<strong>Mode:</strong><span class="ml-1">{{ mode }}</span>
								</template>
							</div>
						</div>
					</div>
					<div
						v-for="profile in profileList"
						:key="profile.id"
						class="card ml-5 mt-2"
						:class="{ clickable: profile.score != null, 'clickable-card': profile.score != null }"
						@click="goToProfile(profile)"
					>
						<div v-if="profile.score != null" class="d-flex flex-row align-items-center mt-1 px-2">
							<div class="flex-grow">{{ profile.name }}</div>
							<div class="mr-5">
								{{ fs.medDate(profile.time) }}
							</div>
							<div class="text-lg" style="width: 50px;">
								{{ fs.round1d(profile.score) }}
							</div>
						</div>
						<div
							v-if="profile.score == null"
							class="d-flex flex-row align-items-center mt-1 px-2 text-extra-muted"
						>
							<div class="flex-grow">{{ profile.name }}</div>
							<div class="mr-5">
								Not Completed
							</div>
							<div class="text-lg" style="width: 50px;">
								N/A
							</div>
						</div>
						<template v-if="profile.text">
							<div class="mb-1 px-2">"{{ profile.text }}"</div>
						</template>
					</div>
				</div>
			</div>
		</div>

		<template v-slot:footer>
      <div class="col-6 row no-gutters text-right">
        <div class="dropdown dropdown-menu-right dropup" style="flex:1;">
					<button
						class="btn btn-si-secondary dropdown-toggle"
						type="button"
						id="dropdownMenuButton"
						data-toggle="dropdown"
						aria-haspopup="true"
						aria-expanded="false"
					>
						Export
					</button>
					<div class="dropdown-menu text-md" aria-labelledby="dropdownMenuButton">
						<button class="dropdown-item" @click="dlScores">Scores</button>
						<button class="dropdown-item" @click="dlRankings">Skill & Mindset Rankings</button>
						<!-- <button class="dropdown-item" @click="dlTopStrengths">Top Strengths Report</button> -->
					</div>
				</div>
				<a class="btn btn-si-secondary ml-3" href="#/portfolio">Back</a>
			</div>
		</template>
	</main-page>
</template>

<script>
import MainPage from "@/components/MainPage";
import ConfigSelect from "@/components/ConfigSelect";
import ApexChart from "vue-apexcharts";

import SectionService from "@/services/SectionService";
import ProfileService from "@/services/ProfileService";
import ProgramService from "@/services/ProgramService";
import FileService from "@/services/FileService";
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import BB from "bluebird";
import _ from "lodash";

var categoryNames = {};

export default {
	name: "ProfileList",

	props: ["user"],

	components: { MainPage, ConfigSelect, ApexChart },

	data() {
		return {
			fs: fs,
			loading: true,

			itemOptions: [],
			selectedItem: null,
			selectedTeam: -3,
			selectedStudent: null,
			studentOptions: [],

			chartOptions: {
				chart: {
					id: "chart-profile-list",
					zoom: {
						enabled: false,
					},
					toolbar: {
						tools: {
							download: false,
						},
					}
				},
				title: {
					text: "Profile Progress",
					align: "center",
					style: {
						fontSize: "20px",
					},
				},
				xaxis: {
					categories: ["No Data"],
					labels: {
						show: true,
						style: {
							fontSize: "20px",
						},
					},
					title: {
						text: "Assignments",
						style: {
							fontSize: "20px",
						},
					},
				},
				yaxis: {
					min: 0,
					max: 100,
					tickAmount: 10,
					forceNiceScale: true,
					decimalsInFloat: 1,
					labels: {
						show: true,
						offsetX: -10,
						style: {
							fontSize: "20px",
						},
					},
					title: {
						text: "Rating (100)",
						style: {
							fontSize: "20px",
						},
					},
				},
				tooltip: {
					style: {
						fontSize: "20px",
					},
					x: {
						formatter: (value) => {
							let catName = categoryNames[value];
							return catName || value;
						},
					},
				},
				legend: {
					fontSize: "20px",
				},
				colors: [
					"#2554C7",
					"#66DA26",
					"#E42217",
					"#FF9800",
					"#8D38C9",
					"#EA428B",
					"#3BB9FF",
					"#347235",
					"#848482",
					"#EAC117",
				],
				stroke: {
					curve: "smooth",
				},
				dataLabels: {
					enabled: true,
					style: {
						fontSize: "14px",
						fontWeight: "bold",
					},
					formatter: function (val) {
						if (val == null) {
							return undefined;
						}
						return val.toFixed(1);
					},
				},
				grid: {
					padding: {
						left: 30,
						right: 30,
					},
				},
			},
			chartData: [],
			profileAssignments: null,
			profiles: null,
			profileList: null,
			showStats: false,
			mean: 0,
			median: 0,
			mode: "",
			rangeLow: 0,
			rangeHigh: 0,

			context: null,
			students: null,
			teams: null,
			program: null,

			keyExpanded: false,
		};
	},

	computed: {
		teamOptions() {
			let sortedTeams = _.sortBy(this.teams, "name");

			let metaOptions = [
				{
					name: "All Teams",
					id: -3,
				},
				// {
				// 	name: "No Team",
				// 	id: -2,
				// },
			];

			// if (sortedTeams && sortedTeams.length > 0) {
			// 	metaOptions.push({
			// 		name: "Any Team",
			// 		id: -1,
			// 	});
			// }

			let teamOptions = metaOptions.concat(sortedTeams);
			return teamOptions;
		},
	},

	watch: {
		selectedTeam() {
			this.studentOptions = this.buildStudentOptions();
			this.setChartData(this.profiles);
		},

		selectedStudent() {
			this.loadProfiles();
		},

		selectedItem() {
			this.setChartData(this.profiles);
		},
	},

	created() {
		this.loadData();
	},

	methods: {
		loadData() {
			BB.props({
				context: SectionService.getContextPrefilled(),
				program: ProgramService.getContextProgram(),
			})
				.then((r) => {
					this.loading = false;

					this.setContext(r.context.data);
					this.studentOptions = this.buildStudentOptions();

					this.program = r.program.data;
					this.itemOptions = this.buildItemOptions();

					this.loadProfiles();
				})
				.catch((e) => {
					this.loading = false;
					console.log(e);
					Notie.error("Failed to load context", e);
				});
		},

		setContext(context) {
			this.context = context;
			this.students = context.students;
			this.teams = context.teams;
			this.profileAssignments = fs.sortByDate(context.profiles, "start");

			let studentTeamMap = {};
			_.each(this.teams, (team) => {
				_.each(team.user_ids, (uid) => {
					studentTeamMap[uid] = team;
				});
			});

			_.each(this.students, (student) => {
				student.team = studentTeamMap[student.id];
			});

			this.chartOptions.xaxis.categories = [];
			_.each(this.profileAssignments, (pa, i) => {
				categoryNames[i + 1] = pa.name;
				this.chartOptions.xaxis.categories.push(i + 1);
			});
			this.$refs.chart.updateOptions(this.chartOptions);

			_.each(this.students, (student) => {
				ProfileService
					.getUserProfiles(this.context.id, student.id)
					.then((r) => {
						console.log(r.data);
						student.profiles = r.data.filter(profile => {
							for (var pa of this.profileAssignments) {
								if (pa.id == profile.assignment_id) {
									return true
								}
							}
							return false
						});
					})
					.catch((e) => {
						console.log(e);
					});
			});
		},

		loadProfiles() {
			let call = null;
			if (this.selectedStudent) {
				call = ProfileService.getUserProfiles(this.context.id, this.selectedStudent);
			} else if (this.selectedTeam == -3) {
				call = ProfileService.getSectionProfiles(this.context.id);
			} else if (this.selectedTeam == -2) {
				call = ProfileService.getNoTeamProfiles(this.context.id);
			} else if (this.selectedTeam == -1) {
				call = ProfileService.getHasTeamProfiles(this.context.id);
			} else {
				call = ProfileService.getTeamProfiles(this.context.id, this.selectedTeam);
			}

			call.then((r) => {
				this.profiles = r.data;
				this.setChartData(this.profiles);
			}).catch((e) => {
				console.log(e);
				Notie.error("Failed to load profiles", e);
			});
		},

		buildStudentOptions() {
			if (!(this.students && this.students.length > 0)) {
				return [];
			}

			let studentOptions = _.sortBy(this.students, ["name.last", "name.first"]);

			// All students, no filtering
			if (!this.selectedTeam || this.selectedTeam == -3) {
				return studentOptions;
			}

			// Filter for no team defined
			if (this.selectedTeam == -2) {
				return _.filter(studentOptions, (s) => {
					return !s.team;
				});
			}

			// Filter for any team defined
			if (this.selectedTeam == -1) {
				return _.filter(studentOptions, (s) => {
					return s.team;
				});
			}

			// Filter for specific team
			return _.filter(studentOptions, (s) => {
				return s.team && s.team.id == this.selectedTeam;
			});
		},

		buildItemOptions() {
			let itemOptions = [];
			if (
				!(
					this.program &&
					this.program.profile_config &&
					this.program.profile_config.questions &&
					this.program.profile_config.questions.length > 0
				)
			) {
				return itemOptions;
			}

			let seq = 0;

			_.each(this.program.profile_config.strengths, (strength) => {
				// let desc = this.getFocusDesc(this.program.profile_config.questions, strength.id);
				itemOptions.push({
					id: strength.id,
					name: strength.name,
					// desc: desc,
					sequence: seq++,
					active: true,
					strength: true,
				});
			});

			// _.each(this.program.profile_config.questions, (question, i) => {
			// 	itemOptions.push({
			// 		id: question.id,
			// 		name: `Question ${i + 1}`,
			// 		desc: question.text,
			// 		sequence: seq++,
			// 		active: false,
			// 	});
			// });

			return itemOptions;
		},

		getFocusDesc(questions, strengthID) {
			let questionIndices = [];
			_.each(questions, (q, i) => {
				if (q.strength_id == strengthID) {
					questionIndices.push(i + 1);
				}
			});

			if (questionIndices.length == 0) {
				return "No Questions";
			} else if (questionIndices.length == 1) {
				return `Question ${questionIndices[0]}`;
			} else {
				return `Questions ${questionIndices.join(", ")}`;
			}
		},

		structureProfiles(profiles) {
			let includedStudents = {};
			_.each(this.studentOptions, (so) => {
				includedStudents[so.id] = true;
			});

			let structured = {};
			_.each(profiles, (p) => {
				if (!includedStudents[p.user_id]) {
					return;
				}

				if (!structured[p.user_id]) {
					structured[p.user_id] = {
						profiles: {},
					};
				}

				structured[p.user_id].profiles[p.assignment_id] = p;
			});

			if (this.selectedStudent && !structured[this.selectedStudent]) {
				structured[this.selectedStudent] = { profiles: [] };
			}

			return structured;
		},

		setChartData(profiles) {
			let structuredProfiles = this.structureProfiles(profiles);
			let profileList = [];
			let chartData = [];
			let scoreSet = [];

			_.each(structuredProfiles, (pu, userID) => {
				let user = this.getUser(userID);
				let scores = [];
				_.each(this.profileAssignments, (pa) => {
					let profile = pu.profiles[pa.id];
					let profileListing = this.getProfileListing(profile, pa, this.selectedItem);
					console.log("profile assignment", pa, profileListing);
					profileList.push(profileListing);
					scores.push(profileListing.score);
					scoreSet = scoreSet.concat(profileListing.scoreSet);
				});

				chartData.push({
					name: fs.displayName(user.name),
					data: scores,
				});
			});

			// Only show list of profiles if a specific student is selected
			if (this.selectedStudent) {
				this.profileList = profileList;
				this.showStats = false;
			} else {
				this.profileList = [];
				console.log("CALCULATE STATS", scoreSet);
				[this.mean, this.median, this.mode, this.rangeLow, this.rangeHigh] = fs.statistics(scoreSet);
				this.showStats = true;
			}

			this.chartData = chartData;
			this.chartOptions.legend.show = this.chartData.length <= 10;
			this.$refs.chart.updateOptions(this.chartOptions);
		},

		getProfileListing(profile, pa, item) {
			if (!profile) {
				return {
					id: pa.id,
					name: pa.name,
					missing: true,
					score: null,
					scoreSet: [],
				};
			}

			if (item && !item.strength) {
				let resp = _.find(profile.responses, { item_id: item.id });
				if (resp) {
					return {
						id: pa.id,
						profile: profile,
						name: pa.name,
						itemName: item.name,
						score: resp.score,
						text: resp.reasoning,
						scoreSet: [resp.score],
					};
				} else {
					return {
						id: pa.id,
						profile: profile,
						name: pa.name,
						missing: true,
						itemName: item.name,
						score: null,
						scoreSet: [],
					};
				}
			}
			if (item && item.strength) {
				let focusQuestions = {};
				_.each(_.filter(this.program.profile_config.questions, { strength_id: item.id }), (q) => {
					focusQuestions[q.id] = true;
				});
				let numItems = 0;
				let compositeScore = 0;
				let scoreSet = [];
				_.each(profile.responses, (resp) => {
					if (focusQuestions[resp.item_id]) {
						numItems++;
						compositeScore += resp.score || 0;
						if (resp.score != undefined) {
							scoreSet.push(resp.score);
						}
					}
				});
				let avgScore = numItems > 0 ? compositeScore / numItems : 0;
				return {
					id: pa.id,
					profile: profile,
					name: pa.name,
					itemName: item.name,
					score: avgScore,
					scoreSet: scoreSet,
				};
			}

			let numItems = 0;
			let compositeScore = 0;
			_.each(profile.responses, (resp) => {
				numItems++;
				compositeScore += resp.score || 0;
			});
			let avgScore = numItems > 0 ? compositeScore / numItems : 0;
			return {
				id: pa.id,
				profile: profile,
				name: pa.name,
				time: profile.created_at,
				score: avgScore,
				scoreSet: [avgScore],
			};
		},

		getUser(userID) {
			return _.find(this.students, { id: userID });
		},

		getStatus() {
			return "Partial";
		},

		goToProfile(listing) {
			let p = listing.profile;
			if (!p) {
				return;
			}
			this.$router.push(`profile_result/${p.user_id}/${p.assignment_id}`);
		},

		getTitle() {
			if (!this.selectedTeam) {
				return "(Unknown)";
			}

			let team = _.find(this.teamOptions, { id: this.selectedTeam });
			let title = team.name;

			if (this.selectedItem) {
				title += `, ${this.selectedItem.name}`;
			}

			return title;
		},

		getColor(student) {
			if (student.profiles) {
				if (student.profiles.length == 0) {
					return "nodata";
				}
				if (student.profiles.length == this.profileAssignments.length) {
					return "done"
				}
				if (student.profiles.length > 0 && student.profiles.length < this.profileAssignments.length) {
					return "partial";
				}
			}
		},

		dlScores() {
			FileService.exportProfileScores(
				this.context,
				this.program,
				this.profileAssignments,
				this.profiles,
				this.students
			);
		},

		dlRankings() {
			FileService.exportProfileRankings(
				this.context,
				this.program,
				this.profileAssignments,
				this.profiles,
				this.students
			);
		},

		// dlTopStrengths() {
		// 	FileService.exportProfileReport(
		// 		this.context,
		// 		this.program,
		// 		this.profileAssignments,
		// 		this.profiles
		// 	);
		// },
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.zone-padding {
	padding: 3rem;
}

.btn-expand::after {
	margin-top:-25px;
}
.key-color {
	height:12px;
	width:12px;
	border-radius:6px;
	float:left;
	margin-top:4px;
	margin-right:2px;
}
.key-text {
	float:left;
	margin-right:7px;
}
.clickable-card {
	transition: background-color 0.15s ease-in-out;
	background-color: #ffffff;
}
.clickable-card.selected {
	box-shadow: 0 0 0 3px #d0d0d0;
	background-color: #f7f7f7;
}
.clickable-card.done {
	border-color:#509050;
	background-color:#f0fff0;
}
.clickable-card.done.selected {
	background-color:#f0f7f0;
}
.clickable-card.done:hover {
	background-color:#e0f0e0;
}
.clickable-card.partial {
	border-color:#909050;
	background-color:#fffff0;
}
.clickable-card.partial.selected {
	background-color:#f7f7f0;
}
.clickable-card.partial:hover {
	background-color:#f0f0e0;
}
.clickable-card.nodata {
	background-color:#e0e0e0;
}
.clickable-card.nodata:hover {
	background-color:#e0e0e0;
}
.clickable-card:hover {
	background-color: #f0f0f0;
}
.clickable-card:active {
	background-color: #e0e0e0;
}
</style>
